import type { CaseId, Force, Pt, WallResult } from "@tasduvar/engine";

/** Basınç diyagramı düzlemi: alt/üst nokta ve statik + dinamik yoğunluklar (kN/m², yamuk eşdeğeri). 2B katman ve 3B sahne ortak kullanır. */
export interface PressurePlane { bot: Pt; top: Pt; sBot: number; sTop: number; dBot: number; dTop: number }

/**
 * Sanal düzlem yönteminde tek üçgen (statik) + üçgen (dinamik artım) eşdeğeri; back-face yönteminde her kademe yüzü için
 * o yüzün bileşkelerinden türetilen yamuk. Görsel amaçlıdır; sayısal değerler kuvvet tablosundadır.
 */
export function pressurePlanes(result: WallResult, caseId: CaseId): { planes: PressurePlane[]; pMax: number; Ka: number } | null {
  const c = result.cases[caseId]; if (!c) return null;
  const g = result.geometry;
  const mag = (fs: Force[], kind: "earthStatic" | "earthDynamic"): number => fs.filter((f) => f.kind === kind).reduce((s, f) => s + Math.hypot(f.H, f.V), 0);
  const Ka = c.coefficients.perLayer[0]?.Kstatic ?? 0;
  const planes: PressurePlane[] = [];
  if (g.pressureMethod === "back-face" && g.backFaces.length > 0) {
    for (const f of g.backFaces) {
      const fs = c.forces.filter((x) => new RegExp(`^Pad?${f.index}\\.`).test(x.id));
      const h = f.top.y - f.bottom.y; if (h < 1e-9) continue;
      const zt = f.yC - f.top.y, zb = f.yC - f.bottom.y;
      const shape = (F: number): [number, number] => { const k = (2 * F) / ((zb * zb - zt * zt) || 1e-9); return [k * zb, k * zt]; };   // p = k·z eşdeğeri
      const [sBot, sTop] = shape(mag(fs, "earthStatic")); const [dBot, dTop] = shape(mag(fs, "earthDynamic"));
      planes.push({ bot: f.bottom, top: f.top, sBot, sTop, dBot, dTop });
    }
  } else {
    const H = g.Hp;
    planes.push({ bot: g.heel, top: g.virtualPlane.top, sBot: (2 * mag(c.forces, "earthStatic")) / H, sTop: 0, dBot: (2 * mag(c.forces, "earthDynamic")) / H, dTop: 0 });
  }
  const pMax = Math.max(...planes.map((p) => p.sBot + p.dBot), 1e-9);
  return { planes, pMax, Ka };
}
